import axios from 'axios';
import { toast } from '../components/ui/notify';

// Get the current hostname
const hostname = window.location.hostname;

// Determine the base URL based on environment
const BASE_URL = process.env.REACT_APP_API_URL || (hostname === 'localhost' ? 'http://localhost:4000' : 'https://api.nxgenvarsity.com');

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let authRedirectInProgress = false;

/**
 * Axios default JSON Content-Type makes transformRequest stringify FormData as JSON,
 * which breaks multer and profile file uploads. Clear it so the browser sets
 * multipart/form-data with a proper boundary.
 */
function clearContentTypeForFormData(config) {
  if (typeof FormData === 'undefined' || !(config.data instanceof FormData)) return;
  const h = config.headers;
  if (!h) return;
  if (typeof h.delete === 'function') {
    h.delete('Content-Type');
    h.delete('content-type');
  } else {
    delete h['Content-Type'];
    delete h['content-type'];
  }
}

// Add request interceptor with better error logging
api.interceptors.request.use(
  (config) => {
    clearContentTypeForFormData(config);
    if (!config.skipAuth) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = token;
      }
    } else if (config.headers) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }
    return config;
  },
  (error) => {
    console.error('[ApiController] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor with better error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors (token expired)
    if (error.response && error.response.status === 401) {
      const requestUrl = error.config?.url || '';
      const isLoginEndpoint = requestUrl.includes('/login');
      const isPublicAccountDeletion = requestUrl.includes('/public/account-deletion');

      if (isLoginEndpoint || isPublicAccountDeletion || error.config?.skipAuth) {
        return Promise.reject(error);
      }

      const sessionExpiredMessage =
        error.response?.data?.error_msg ||
        error.response?.data?.data?.error_msg ||
        'Your session has expired. Please log in again.';
      const isOrganizationInactive = error.response?.data?.error_type === 'ORG_INACTIVE';

      if (authRedirectInProgress) {
        return Promise.reject(error);
      }

      authRedirectInProgress = true;

      // Clear any stored tokens
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('role');
      localStorage.removeItem('organization_details');

      // Preserve the reason once, then clear the rest of the session data.
      sessionStorage.clear();
      sessionStorage.setItem(
        'auth_redirect_message',
        isOrganizationInactive
          ? sessionExpiredMessage
          : 'Your session has expired. Please log in again.'
      );

      // Redirect to login page
      // Check if we're in a browser environment
      if (typeof window !== 'undefined') {
        // Get current domain for redirect
        const currentDomain = window.location.hostname;
        const protocol = window.location.protocol;
        const port = window.location.port ? `:${window.location.port}` : '';

        const loginUrl = `${protocol}//${currentDomain}${port}/auth/login`;
        if (isOrganizationInactive) {
          toast.error(sessionExpiredMessage, {
            position: 'top-center',
            autoClose: 2200,
          });
          window.setTimeout(() => {
            window.location.href = loginUrl;
          }, 2300);
        } else {
          window.location.href = loginUrl;
        }
      }
    }

    console.error('[ApiController] Response error:', error);
    return Promise.reject(error);
  }
);

class ApiController {
  static async get(endpoint, params = {}) {
    try {
      const response = await api.get(endpoint, { params });
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw ApiController.toError(error);
    }
  }

  static async post(endpoint, data = {}, options = {}) {
    try {
      const response = await api.post(endpoint, data, options);
      return response.data;
    } catch (error) {
      // Don't log expected errors like 404 (post not found) or course not found
      if (!(error.response && error.response.status === 404 &&
            (error.response.data?.error_msg === 'Post not found' ||
             error.response.data?.error_msg === 'Classroom not found or has been deleted' ||
             error.response.data?.error_msg === 'You no longer have access to this classroom' ||
             error.response.data?.error_msg === 'This assignment is not available' ||
             error.response.data?.error_msg === 'This assessment is not available' ||
             error.response.data?.error_msg === 'This live class is not available' ||
             error.response.data?.error_msg === 'This resource is not available' ||
             error.response.data?.error_msg === 'Classroom not found' ||
             error.response.data?.message === 'Course not found or inactive' ||
             error.response.data?.message === 'This course is not available' ||
             error.response.data?.message === 'You no longer have access to this course' ||
             error.response.data?.message === 'Email is not registered.'))) {
        console.error('[ApiController] POST request error:', error);
      }
      ApiController.handleError(error);
      throw ApiController.toError(error);
    }
  }

  static async put(endpoint, data = {}) {
    try {
      const response = await api.put(endpoint, data);
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw ApiController.toError(error);
    }
  }

  static async patch(endpoint, data = {}) {
    try {
      const response = await api.patch(endpoint, data);
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw ApiController.toError(error);
    }
  }

  static async delete(endpoint, data = null) {
    try {
      const config = data ? { data } : {};
      const response = await api.delete(endpoint, config);
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw ApiController.toError(error);
    }
  }

  static async uploadFile(endpoint, formData, additionalData = {}, onProgress = null) {
    try {
      // Add any additional data to the existing FormData
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      const response = await api.post(endpoint, formData, {
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(percentCompleted);
            }
          : undefined,
      });
      return response.data;
    } catch (error) {
      console.error('[ApiController] Upload failed with error:', error);
      ApiController.handleError(error);
      throw ApiController.toError(error);
    }
  }

  static async uploadFileWithMethod(endpoint, formData, method = 'POST', additionalData = {}, onProgress = null) {
    try {
      // Add any additional data to the existing FormData
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      // Choose the appropriate axios method
      const axiosMethod = method.toLowerCase() === 'put' ? api.put :
                         method.toLowerCase() === 'patch' ? api.patch :
                         api.post;

      const response = await axiosMethod(endpoint, formData, {
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(percentCompleted);
            }
          : undefined,
      });
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw ApiController.toError(error);
    }
  }

  static async uploadMultipleFiles(endpoint, files, additionalData = {}, onProgress = null) {
    try {
      const formData = new FormData();

      files.forEach((file, index) => {
        formData.append(`files[${index}]`, file);
      });

      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      const response = await api.post(endpoint, formData, {
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(percentCompleted);
            }
          : undefined,
      });
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw ApiController.toError(error);
    }
  }

  /**
   * Return a normalized Error with a readable message so React/UI never see "[object Object]".
   */
  static toError(error) {
    if (error?.response?.data) {
      const d = error.response.data;
      const msg =
        d.error_msg ||
        d.message ||
        (d.error && typeof d.error === 'object' ? d.error.message : d.error) ||
        (typeof d === 'string' ? d : null);
      if (msg) return new Error(msg);
      return new Error(`Request failed (${error.response.status})`);
    }
    if (error instanceof Error && error.message) {
      return error;
    }
    if (error?.message) {
      return new Error(error.message);
    }
    return new Error('Request failed');
  }

  static handleError(error) {
    // Don't log expected errors like 404 (post not found) or course not found to reduce console noise
    if (error.response && error.response.status === 404 &&
        (error.response.data?.error_msg === 'Post not found' ||
         error.response.data?.error_msg === 'Classroom not found or has been deleted' ||
         error.response.data?.error_msg === 'You no longer have access to this classroom' ||
         error.response.data?.error_msg === 'This assignment is not available' ||
         error.response.data?.error_msg === 'This assessment is not available' ||
         error.response.data?.error_msg === 'This live class is not available' ||
         error.response.data?.error_msg === 'This resource is not available' ||
         error.response.data?.error_msg === 'Classroom not found' ||
         error.response.data?.message === 'Course not found or inactive' ||
         error.response.data?.message === 'This course is not available' ||
         error.response.data?.message === 'You no longer have access to this course' ||
         error.response.data?.message === 'Email is not registered.')) {
      // This is an expected error, don't log it
      return;
    }

    // Don't log 401 errors as they're handled in the interceptor
    if (error.response && error.response.status === 401) {
      return;
    }

    if (error.response) {
      console.error('[ApiController] Response Error:', error.response.data);
      console.error('[ApiController] Status:', error.response.status);
      console.error('[ApiController] Headers:', error.response.headers);
    } else if (error.request) {
      console.error('[ApiController] Request Error:', error.request);
    } else {
      console.error('[ApiController] Error:', error.message);
    }
  }
}

export const GET = ApiController.get;
export const POST = ApiController.post;
export const PUT = ApiController.put;
export const PATCH = ApiController.patch;
export const DELETE = ApiController.delete;
export const UPLOAD_FILE = ApiController.uploadFile;
export const UPLOAD_FILE_WITH_METHOD = ApiController.uploadFileWithMethod;
export const UPLOAD_MULTIPLE_FILES = ApiController.uploadMultipleFiles;

export default ApiController;
